import { useEffect, useState } from 'react';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MDButton from '@components/MDButton';
import { CurrencyInput } from '@components/CurrencyInput';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { LeadResponse } from '../../kanban-board/services/responses/lead.response';
import { MultichannelService } from '../../../Multichannel/services/multichannel.service';
import { StandaloneAutoCompleteSelect } from '@components/StandaloneAutoCompleteSelect';
import { LeadPayment, LeadPaymentService, PaymentProvider } from '../services/lead-payment.service';

interface Props {
    lead: LeadResponse;
    /// Os meios configurados. Com mais de um, o modal pergunta por onde.
    providers: PaymentProvider[];
    open: boolean;
    onClose: () => void;
    onCreated: () => void;
}

/// Gera o link do Mercado Pago; ao aprovar, o Mercado Pago avisa o CRM e o lead vira ganho sozinho.
export const LeadPaymentModal = ({ lead, providers, open, onClose, onCreated }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [amount, setAmount] = useState<number | null>(null);
    const [description, setDescription] = useState('');
    const [provider, setProvider] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [created, setCreated] = useState<LeadPayment | null>(null);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        if (!open) return;

        setAmount(lead.value ?? null);
        setDescription(lead.procedureName ?? '');
        setProvider(providers.length === 1 ? providers[0].provider : null);
        setCreated(null);
        setSent(false);
    }, [open, lead.id]);

    const field = {
        '& .MuiOutlinedInput-root': {
            bgcolor: cc.surfaceBg,
            borderRadius: '8px',
            color: cc.textPrimary,
            '& fieldset': { borderColor: cc.border },
        },
        '& .MuiInputBase-input': { color: cc.textPrimary },
        '& .MuiFormLabel-root': { color: cc.textSecondary },
    };

    const create = async () => {
        if (!amount || amount <= 0) {
            showAlert(t('cobranca.valorObrigatorio'), 'warning', 3000);
            return;
        }

        if (!provider) {
            showAlert(t('cobranca.meioObrigatorio'), 'warning', 3000);
            return;
        }

        if (!description.trim()) {
            showAlert(t('cobranca.descricaoObrigatoria'), 'warning', 3000);
            return;
        }

        setSaving(true);

        try {
            const res = await new LeadPaymentService().create(lead.id, {
                provider,
                amount,
                description: description.trim(),
            });
            setCreated(res.data.data);
            onCreated();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 5000);
        } finally {
            setSaving(false);
        }
    };

    const copy = () =>
        navigator.clipboard
            .writeText(created?.checkoutUrl ?? '')
            .then(() => showAlert(t('integracoes.copiado'), 'success', 1500));

    const sendToConversation = async () => {
        if (!created || !lead.chatwootConversationId) return;

        setSaving(true);

        try {
            await new MultichannelService().send(
                lead.chatwootConversationId,
                t('cobranca.mensagem', { descricao: created.description, link: created.checkoutUrl }),
            );
            setSent(true);
            onCreated();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 5000);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ pb: 0.5 }}>
                {t('cobranca.titulo')}
                <Typography variant="caption" sx={{ display: 'block', color: cc.textSecondary }}>
                    {lead.contactName}
                </Typography>
            </DialogTitle>

            <DialogContent dividers sx={{ display: 'grid', gap: 2, pt: 2 }}>
                {created ? (
                    <>
                        <Typography sx={{ fontSize: 13, color: cc.textSecondary }}>
                            {t('cobranca.linkGerado')}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                p: 1.25,
                                borderRadius: '8px',
                                border: `1px solid ${cc.border}`,
                                bgcolor: cc.surfaceBg,
                            }}
                        >
                            <Typography sx={{ flex: 1, fontSize: 13, color: cc.textPrimary, wordBreak: 'break-all' }}>
                                {created.checkoutUrl}
                            </Typography>
                            <Tooltip title={t('integracoes.copiar')}>
                                <IconButton size="small" onClick={copy} sx={{ color: cc.textSecondary }}>
                                    <ContentCopyIcon sx={{ fontSize: '17px !important' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Typography sx={{ fontSize: 12.5, color: cc.textMuted }}>
                            {t('cobranca.ganhoAutomatico')}
                        </Typography>
                    </>
                ) : (
                    <>
                        {providers.length > 1 && (
                            <StandaloneAutoCompleteSelect
                                options={providers.map((p) => ({ value: p.provider, label: p.name }))}
                                value={provider}
                                onChange={(value) => setProvider((value as string) ?? null)}
                                placeholder={t('cobranca.porOnde')}
                            />
                        )}
                        <CurrencyInput
                            fullWidth
                            autoFocus
                            value={amount}
                            onChange={setAmount}
                            label={t('crmKanban.valor')}
                            sx={field}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label={t('cobranca.descricao')}
                            placeholder={t('cobranca.descricaoPlaceholder')}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            inputProps={{ maxLength: 256 }}
                            helperText={t('cobranca.descricaoAjuda')}
                            sx={field}
                        />
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <MDButton variant="gradient" color="dark" onClick={onClose}>
                    {created ? t('general.fechar') : t('general.cancelar')}
                </MDButton>
                {!created && (
                    <MDButton variant="gradient" color="success" onClick={create} disabled={saving}>
                        {t('cobranca.gerarLink')}
                    </MDButton>
                )}
                {created && lead.chatwootConversationId && (
                    <MDButton
                        variant="gradient"
                        color="success"
                        onClick={sendToConversation}
                        disabled={saving || sent}
                    >
                        {sent ? t('cobranca.enviado') : t('cobranca.enviarNaConversa')}
                    </MDButton>
                )}
            </DialogActions>
        </Dialog>
    );
};
